import React, { useState, useEffect, useMemo } from "react";
import DateRangePicker from "./DateRangePicker";
import moment from "moment";
import TaskEmployees from "./TaskEmployees";
import TaskTimeline from "./TaskTimeline ";
import { CalendarRange } from "lucide-react";
import { useTotalStaff } from "../Use-auth";
import { getStatusColor } from "../Pages/utils/taskUtils";

export default function TeamDetialis({
  project,
  activeProjectId,
  user,
  activeSubProjectId,
}) {
  const { data: totalStaff } = useTotalStaff();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tick, setTick] = useState(0);

  const activeProject = project?.find((p) => p._id === activeProjectId);

  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    return { startDate: today, endDate: today };
  });

  /* ---------------- GET TASK SOURCE ---------------- */
  const taskSource = useMemo(() => {
    if (!activeProject) return null;

    if (!activeProject.isMultiProject) {
      return activeProject.statusTask || {};
    }

    if (activeProject.isMultiProject && Array.isArray(activeProject.subProjects)) {

      if (activeSubProjectId) {
        const selectedSubProject = activeProject.subProjects.find(
          (sub) => sub._id === activeSubProjectId
        );

        return selectedSubProject?.statusTask || {};
      }

      const merged = {};

      activeProject.subProjects.forEach((sub) => {
        Object.entries(sub.statusTask || {}).forEach(([status, tasks]) => {
          if (!merged[status]) merged[status] = [];

          merged[status].push(
            ...(tasks || []).map((task) => ({
              ...task,
              subProjectName: sub.projectName,
              subProjectId: sub._id
            }))
          );
        });
      });

      return merged;
    }

    return {};
  }, [activeProject, activeSubProjectId]);

  /* ---------------- FILTERED TASKS ---------------- */
  const filteredTasks = useMemo(() => {
    if (!taskSource) return {};

    const filtered = {};

    Object.entries(taskSource).forEach(([status, tasks]) => {
      filtered[status] = (tasks || []).filter((task) => {
        if (!dateRange) return true;

        const created = moment(task.createdAt);
        return (
          created.isSameOrAfter(moment(dateRange.startDate), "day") &&
          created.isSameOrBefore(moment(dateRange.endDate), "day")
        );
      });
    });

    return filtered;
  }, [taskSource, dateRange, tick]);

  /* ---------------- TOTALS ---------------- */
  /* ---------------- TOTALS ---------------- */
  const { totalTasks, totalTime } = useMemo(() => {
    let tasksCount = 0;
    let totalSeconds = 0;

    Object.values(filteredTasks).forEach((tasks) => {
      tasksCount += tasks.length;

      tasks.forEach((task) => {
        if (!task.timeline) return;

        task.timeline.forEach((entry) => {
          if (!entry.startTime) return;

          const start = moment(entry.startTime);
          const end = entry.endTime ? moment(entry.endTime) : moment();

          if (!start.isValid() || !end.isValid()) return;

          if (
            !start.isSameOrAfter(moment(dateRange.startDate), "day") ||
            !start.isSameOrBefore(moment(dateRange.endDate), "day")
          ) {
            return;
          }

          const diff = end.diff(start, "seconds");

          if (diff > 0) {
            totalSeconds += diff;
          }
        });
      });
    });

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return {
      totalTasks: tasksCount,
      totalTime: `${hours}h ${minutes}m ${seconds}s`,
    };
  }, [filteredTasks, dateRange, tick]);

  /* ---------------- TIMER ---------------- */
  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  /* ---------------- UI ---------------- */
  return (
    <div className="p-4">
      <div className="flex justify-end mb-4">
        <div
          className="cursor-pointer border px-3 py-2 rounded-md text-sm bg-white flex gap-2"
          onClick={() => setShowDatePicker(true)}
        >
          <CalendarRange size={18} />
          {moment(dateRange.startDate).format("DD-MM-YYYY")} →
          {moment(dateRange.endDate).format("DD-MM-YYYY")}
        </div>

        <DateRangePicker
          open={showDatePicker}
          selectedRange={dateRange}
          onApply={(range) => range && setDateRange(range)}
          onClose={() => setShowDatePicker(false)}
        />
      </div>

      <div className="flex px-3 py-2 mt-5 rounded-md font-medium text-gray-500 text-[14px] bg-gray-100">
        <div className="w-2/5">Projects</div>
        <div className="w-1/5">Assigned</div>
        <div className="w-1/5">Due Date</div>
        <div className="w-1/5">Timer</div>
        <div className="w-1/5">Status</div>
      </div>

      <div className="mt-2">
        {Object.values(filteredTasks).flat().length === 0 && (
          <div className="text-gray-500 text-sm p-3">No tasks found.</div>
        )}

        {Object.entries(filteredTasks).map(([status, tasks]) =>
          tasks.map((task) => (
            <div
              key={task._id}
              className="flex px-3 py-2 items-center text-sm text-gray-700 border-b bg-white hover:bg-gray-100"
            >
              <div className="w-2/5 text-[14px] text-gray-800">
                <div>
                  <div className="max-w-[300px] truncate">{task.title}</div>

                  {task.subProjectName && (
                    <span className="ml-2 px-2 py-[2px] text-[10px] rounded bg-blue-100 text-blue-700">
                      {task.subProjectName}
                    </span>
                  )}
                </div>              </div>

              <div className="w-1/5">
                <TaskEmployees
                  selected={task.assignedEmployees?.map((e) => e._id) || []}
                  employees={totalStaff}
                  showDropdown={false}
                  onChange={() => { }}
                />
              </div>

              <div className="w-1/5">
                {task.dueDate
                  ? moment(task.dueDate).format("DD-MM-YYYY")
                  : "-"}
              </div>

              <div className="w-1/5">
                <TaskTimeline task={task} user={user} showControls={false} employee={totalStaff}
                />
              </div>

              <div className="w-1/5">
                <div className={`capitalize inline-block text-[12px] sm:text-[14px] px-2 py-1 rounded ${getStatusColor(status)}`}>
                  {status.replace("_", " ")}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="sticky bottom-0 bg-gray-100 px-4 py-[20px] font-semibold border-t border-gray-300 flex justify-between">
        <span>Total Tasks: {totalTasks}</span>
        <span>Total Time: {totalTime}</span>
      </div>
    </div>
  );
}
